import React from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Label, SubmitBtn, TextInput } from "../styled/auth";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { handleCompanyCode } from "../../redux/reducers/authReducer";
import { unwrapResult } from "@reduxjs/toolkit";

const BusinessIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);

const Submit = styled(SubmitBtn)`
  width: auto;
`;
export default function CompanyLogin(props) {
  const { setStep, setLoading, isMobile } = props;
  const { t } = useTranslation("auth");
  const schema = yup
    .object({
      companyCode: yup.string().required(t("CompanyLogin.error")),
    })
    .required();
  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      companyCode: "",
    },
    resolver: yupResolver(schema),
  });
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = methods;
  const dispatch = useDispatch();
  const submitForm = (data) => {
    setLoading(true);
    dispatch(
      handleCompanyCode({
        code: data.companyCode,
        setLoading: setLoading,
        setStep: setStep,
      }),
    )
      .then(unwrapResult)
      .then((data) => {
        setLoading(false);
        if (data === undefined) {
          setError("companyCode", { message: t("CompanyLogin.error_invalid") });
          setLoading(false);
        }
      });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitForm)} style={{ width: "100%" }}>
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Header */}
          <Typography
            variant="h4"
            sx={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#1a1a2e",
              mb: 2,
            }}
          >
            {t("CompanyLogin.header")}
          </Typography>

          {/* Company Code Field */}
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#6b7280",
                mb: 1,
              }}
            >
              {t("CompanyLogin.label")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: "#fff",
                borderRadius: "12px",
                padding: "12px 16px",
              }}
            >
              <BusinessIcon />
              <TextInput
                name="companyCode"
                disableUnderline
                placeholder={t("CompanyLogin.placeholder")}
                {...register("companyCode")}
                sx={{
                  flex: 1,
                  padding: "0 16px",
                  background: "transparent",
                  border: "none",
                  fontSize: "14px",
                }}
              />
            </Box>
            {errors.companyCode && (
              <Typography sx={{ color: "#ef4444", fontSize: "12px", mt: 1 }}>
                {errors.companyCode.message}
              </Typography>
            )}
          </Box>

          {/* Next Button */}
          <Submit
            type="submit"
            sx={{
              width: "100%",
              padding: "14px",
              fontSize: "16px",
              fontWeight: 600,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#fff",
              textTransform: "none",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
              "&:hover": {
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                transform: "translateY(-1px)",
              },
            }}
          >
            {t("CompanyLogin.next")}
          </Submit>
        </Box>
      </form>
    </FormProvider>
  );
}
